import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {Compbaser} from "../../../components/compbaser";
import {StoreSelectorsService} from "../../../services";
import { IPlayerStatistic } from 'app/interfaces';
import * as Highcharts from 'highcharts/highstock';

interface IPlayerStatisticChartData {
    data?: Array<IPlayerStatistic>;
    colors?: Array<any>;
    fields?: Array<string>;
}

@Component({
    selector: 'app-station-statistic-chart',
    templateUrl: './station-statistic-chart.component.html',
    styleUrls: ['./station-statistic-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StationStatisticChartComponent extends Compbaser implements OnInit {
    isHighcharts = typeof Highcharts === 'object'; // new
    Highcharts: typeof Highcharts = Highcharts;
    chartOptions: Highcharts.Options;
    chartOptions2: Highcharts.Options;
    chartConstructor: string = 'stockChart';

    isCount = true;
    isReady = false;
    yAxisLabel = 'Count';

    pieChartCounts = [];
    pieChartDuration = [];
    dotChartData = [];
    colors = [];
    fields: Array<string> = [];
    data: IPlayerStatisticChartData;

    @Input() 
    set chartData(_data: IPlayerStatisticChartData) {
        this.data = _data;
        setTimeout(() => {
            this.initChart();
        }, 111);
    }

    @Input() chartType: string;

    constructor(
        public storeSelector: StoreSelectorsService,
        private cd: ChangeDetectorRef
    ) {
        super();
    }

    ngOnInit() {       
        // this.initChart();
    }

    private generateChartData() {
        this.pieChartCounts = [];
        this.pieChartDuration = [];
        this.dotChartData = [];
        this.colors = [];
        
        this.fields = this.data.fields || [];
        const colors = this.data.colors;
        this.isCount = this.chartType === 'dot-counts';
        this.yAxisLabel = this.isCount ? 'Count': 'Duration';
        const allDates = {};
        const labels = [];

        this.data.data.forEach((row, ind) => {    
            const label = this.getLabel(row, ind + 1);

            if (this.chartType === 'pie') {
                this.pieChartCounts.push({
                    name: label,
                    y: row.count
                });
                this.pieChartDuration.push({
                    name: label,
                    y: row.duration
                });
            } else {
                
                row.group.forEach((row1) => {
                    const dateKey = new Date(+row1.year, +row1.month - 1, +row1.day, +row1.hour).getTime();
                    if (!allDates[dateKey]) {
                        allDates[dateKey] = {};
                    }
                    allDates[dateKey][label] = this.isCount ? +row1.count: +row1.duration;
                });
                labels.push(label);
            }
            this.colors.push(colors[row.uuid] || '#FFFFFF');
        });

        if (this.chartType !== 'pie') {
            labels.forEach((label) => {
                this.dotChartData.push({
                    name: label,
                    id: label,
                    gapSize: 5,
                    marker: {
                        symbol: 'circle'
                    },
                    pointInterval: 3600 * 1000,
                    data: Object.keys(allDates).map((key) => [parseInt(key), allDates[key][label] || null])
                });
            });
        }
    }

    private getLabel(row: IPlayerStatistic, ind: number) {
        if (this.fields.includes('content')) {
            return `${row.stationId}.${row.tag}.${row.content}.${ind}`;
        }
        if (this.fields.includes('category')) {
            return `${row.stationId}.${row.tag}.${row.category}.${ind}`;
        }
        return `${row.stationId}.${row.tag}.${ind}`;
    }

    private initChart() {
        if (!this.data || !this.data.data) {
            this.isReady = false;
            return;
        }
        this.isReady = false;
        this.cd.detectChanges();

        this.generateChartData();

        if (this.chartType === 'pie') {
            const _options = {
                chart: {
                    type: 'pie',
                },
                credits: {
                    enabled: false
                },
                colors: this.colors,
                plotOptions: {
                    series: {
                        allowPointSelect: false,
                        cursor: 'pointer',
                        dataLabels: [{
                            enabled: false,
                        }],
                        showInLegend: false
                    }
                },
                accessibility: {
                    enabled: false
                }
            };

            // options for pie charts count
            this.chartOptions = {
                ..._options,
                title: {
                    text: 'Count'
                },
                series: [
                    {
                        type: 'pie',
                        name: 'Count',
                        data: this.pieChartCounts
                    },
                ],
            };

            // options for pie charts duration
            this.chartOptions2 = {
                ..._options,
                title: {
                    text: 'Duration'
                },
                series: [
                    {
                        type: 'pie',
                        name: 'Duration',
                        data: this.pieChartDuration
                    },
                ],
            };
        } else {
            this.chartOptions = {
                chart: {
                    type: 'scatter',
                    scrollablePlotArea: {
                        minWidth: 600,
                        scrollPositionX: 1
                    }
                },
                time: {
                    useUTC: false
                },
                title: {
                    text: this.yAxisLabel,
                    align: 'center',
                },
                xAxis: {
                    type: 'datetime',
                    title: {
                        text: 'datetime'
                    },
                    minRange: 12 * 3600 * 1000,
                    minTickInterval: 3600 * 1000,
                    // labels: {
                    //     format: '{value}'
                    // },
                    startOnTick: true,
                    endOnTick: true,
                    showLastLabel: true,
                    gridLineWidth: 1
                },
                yAxis: {
                    title: {
                        text: this.yAxisLabel
                    },
                    labels: {
                        format: '{value}'
                    }
                },
                rangeSelector: {
                    buttons: [{
                        type: 'day',
                        count: 1,
                        text: '1d',
                        title: 'View 1 Day'
                    }, {
                        type: 'week',
                        count: 1,
                        text: '1w',
                        title: 'View 1 Week'
                    }, {
                        type: 'all',
                        count: 1,
                        text: 'All'
                    }],
                    selected: 0,
                    allButtonsEnabled: true,
                    inputEnabled: false,
                },
                legend: {
                    enabled: true
                },
                accessibility: {
                    enabled: false
                },
                credits: {
                    enabled: false
                },
                colors: this.colors,
                plotOptions: {
                    scatter: {
                        marker: {
                            radius: 4,
                            symbol: 'circle',
                            states: {
                                hover: {
                                    enabled: true,
                                    lineColor: 'rgb(100,100,100)'
                                }
                            }
                        }
                    },
                    series: {
                        /* compare: 'percent', */
                        showInNavigator: true
                    }
                },
                tooltip: {
                    pointFormat: this.yAxisLabel + ': {point.y}'
                },
                series: this.dotChartData
            };
        }

        this.isReady = true;
        this.cd.detectChanges();
    }
}
